import React from "react";
import { useTranslation } from "react-i18next";
import BookingFlowNotice from "./BookingFlowNotice";

interface SessionRetryNoticeProps {
  /** Optional message returned by the server; falls back to a translated default. */
  readonly message?: string | null;
  /** Re-runs the verification and resumes the in-progress action when it succeeds. */
  readonly onRetry: () => void;
}

/**
 * SessionRetryNotice — shown when verifying the pending booking fails for a
 * transient reason (a network blip, timeout or upstream 5xx) rather than a
 * genuine session expiry. Unlike SessionExpiredNotice it does not lock the flow:
 * it sits above the current step so the guest keeps their progress and can retry.
 */
const SessionRetryNotice: React.FC<SessionRetryNoticeProps> = ({ message, onRetry }) => {
  const { t } = useTranslation();

  return (
    <BookingFlowNotice
      title={t("session_retry_title", "Something went wrong")}
      message={
        message ??
        t(
          "session_retry_message",
          "We couldn't confirm your booking just now. Please check your connection and try again.",
        )
      }
      actionLabel={t("booking_try_again", "Try Again")}
      onAction={onRetry}
    />
  );
};

export default SessionRetryNotice;
